/* eslint-disable no-unused-expressions */
import React, { useEffect, useState } from 'react';
import { TableGral, RowTable, ColTable, Icon, Tooltip, theme, Modal } from 'fpay-ui';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { moneyFormat } from 'helpers';
import { isAllowed } from 'helpers/auth';
import { Menu } from 'components/Menu';
import { MenuItem } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { clearTransactionDetails } from 'redux/transactions/actions';
import TransactionsDetailModal from 'components/Modal/TransactionsDetailModal';
import { format, parseISO, addDays } from 'date-fns';
import { formatDate } from 'helpers/date';
import { useBreakpoints } from '@hooks/NewBreakpoint';
import TransactionsDetailModalMobile from 'components/Modal/TransactionsDetailModalMobile';
import { getEnv } from 'config';

const IconInfo = styled(Icon)`
  margin-left: 5px;
  display: flex;
  padding-top: 20px;
`;

export const TextoFpay = styled.div`
  text-align: center;
  font-size: 12px;
  letter-spacing: 0;
  color: ${theme.colors.gray800};
  opacity: 1;
  line-height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 180px;
  padding: 12px;
`;

const ContainerMount = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-weight: 600;
  text-align: right;
  display: flex;
  align-items: center;
`;

const MountColor = styled.span`
  color: ${(props) => (props.plus ? '#3b9326' : '#333333')};
`;

const TransactionTableGral = ({ dataTable }) => {
  const { t } = useTranslation();
  const [showModalTrxDetails, setShowModalTrxDetails] = useState(false);
  const [headerData, setHeaderData] = useState(null);

  const showOptionForDetails = isAllowed('FALABELLA_TRANSACTIONS_DETAILS');
  const dispatch = useDispatch();

  const { showPaymentDateColumn, country } = getEnv();
  const isPaymentDateColumnEnabled = showPaymentDateColumn === 'true';
  const showColumnBorders = false;
  const cellPaddingX = 16;
  const amountHeaderRightOffset = 30;
  const columnWidths = isPaymentDateColumnEnabled
    ? {
      authCode: '16%',
      reference: '34%',
      scheduledDate: '18%',
      paymentDate: '16%',
      amount: '16%',
    }
    : {
      authCode: '18%',
      reference: '34%',
      scheduledDate: '24%',
      amount: '24%',
    };

  const getColumnCellStyle = ({ isLastColumn = false, isRightAligned = false } = {}) => {
    return {
      paddingLeft: `${cellPaddingX}px`,
      paddingRight: `${cellPaddingX}px`,
      borderLeft: showColumnBorders ? '1px dashed #c8ced6' : undefined,
      borderRight: showColumnBorders && isLastColumn ? '1px dashed #c8ced6' : undefined,
      textAlign: isRightAligned ? 'right' : 'center',
      boxSizing: 'border-box',
    };
  };

  const handleGetTransactionDetails = (orderData) => {
    setShowModalTrxDetails((prevShowModal) => !prevShowModal);
    setHeaderData(orderData);
  };

  useEffect(() => {
    !showModalTrxDetails && dispatch(clearTransactionDetails());
  }, [showModalTrxDetails]);

  const { breakpoint } = useBreakpoints();

  return (
    <>
      <TableGral>
        <RowTable header>
          <ColTable
            header
            align="center"
            justify="center"
            width={columnWidths.authCode}
            style={getColumnCellStyle()}
          >
            {t('transactionsFalabella.authCode')}
          </ColTable>
          <ColTable
            header
            align="center"
            justify="center"
            width={columnWidths.reference}
            style={getColumnCellStyle()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ marginRight: '4px' }}>
                {t('transactionsFalabella.refNumberComplete')}
              </span>
              <Tooltip position="bottom" duration={100} width="190px">
                <IconInfo
                  icon="tooltip-info"
                  width="16px"
                  height="16px"
                  color={`${theme.colors.gray500}`}
                />
                <TextoFpay>{t('transactionsFalabella.tableTooltipText')}</TextoFpay>
              </Tooltip>
            </div>
          </ColTable>
          <ColTable
            header
            align="center"
            justify="center"
            width={columnWidths.scheduledDate}
            style={getColumnCellStyle()}
          >
            {t('transactionsFalabella.scheduledDate')}
          </ColTable>
          {isPaymentDateColumnEnabled && (
            <ColTable
              header
              align="center"
              justify="center"
              width={columnWidths.paymentDate}
              style={getColumnCellStyle()}
            >
              {t('transactionsFalabella.paymentDate')}
            </ColTable>
          )}
          <ColTable
            header
            align="right"
            justify="flex-end"
            width={columnWidths.amount}
            style={getColumnCellStyle({ isLastColumn: true, isRightAligned: true })}
          >
            <span style={{ paddingRight: `${amountHeaderRightOffset}px` }}>
              {t('transactionsFalabella.mountGrossValue')}
            </span>
          </ColTable>
        </RowTable>
        {dataTable?.map((item, index) => (
          <RowTable key={index + item.authorization_id}>
            <ColTable
              align="center"
              justify="center"
              datajs={t('transactionsFalabella.authCode')}
              width={columnWidths.authCode}
              heightColMobile="36px"
              style={getColumnCellStyle()}
            >
              {item.authorization_id}
            </ColTable>
            <ColTable
              align="center"
              justify="center"
              datajs={t('transactionsFalabella.refNumberMin')}
              width={columnWidths.reference}
              heightColMobile="36px"
              style={getColumnCellStyle()}
            >
              {item.details_transaction?.reference_number}
            </ColTable>
            <ColTable
              align="center"
              justify="center"
              datajs={t('transactionsFalabella.scheduledDate')}
              width={columnWidths.scheduledDate}
              heightColMobile="36px"
              style={getColumnCellStyle()}
            >
              {formatDate(item?.event_date, 'DD-MM-YYYY | HH:mm', false, 'UTC', country) || '-'}
            </ColTable>
            {isPaymentDateColumnEnabled && (
              <ColTable
                align="center"
                justify="center"
                datajs={t('transactionsFalabella.paymentDate')}
                width={columnWidths.paymentDate}
                heightColMobile="36px"
                style={getColumnCellStyle()}
              >
                {format(addDays(parseISO(item.payment_date), 1), 'dd-MM-yyyy')}
              </ColTable>
            )}
            <ColTable
              align="right"
              datajs={t('transactionsFalabella.mountGrossValue')}
              justify="flex-end"
              order={-1}
              mobileTitle
              heightColMobile="36px"
              width={columnWidths.amount}
              style={getColumnCellStyle({ isLastColumn: true, isRightAligned: true })}
            >
              <ContainerMount>
                {item.gross_value && item.gross_value >= 0 ? (
                  <MountColor plus>+{moneyFormat(item.gross_value)}</MountColor>
                ) : (
                  <MountColor>{moneyFormat(item.gross_value)}</MountColor>
                )}
                <div
                  style={{
                    width: '24px',
                    paddingTop: '4px',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    paddingLeft: '6px',
                  }}
                >
                  {showOptionForDetails && item.details_transaction?.reference_number && (
                    <Menu
                      icon="ic_menu_dots.svg"
                      imageStyle={{ width: '18px', height: '18px' }}
                      dataTestId="menu-dots"
                      style={{ float: 'right' }}
                      htmlId="transactions-submenu"
                    >
                      <MenuItem
                        id="transactions-see-detail"
                        data-testid="qe-detalle-devolucion_detalle"
                        onClick={() => handleGetTransactionDetails(item)}
                      >
                        Ver detalle
                      </MenuItem>
                    </Menu>
                  )}
                </div>
              </ContainerMount>
            </ColTable>
          </RowTable>
        ))}
      </TableGral>
      {showModalTrxDetails && (
        <>
          {['xs', 'sm', 'ss', 'smm', 'll'].includes(breakpoint) ? (
            <Modal
              fClose={() => setShowModalTrxDetails(false)}
              width={['xs'].includes(breakpoint) ? '370px' : '500px'}
              borderRadius="16px"
              htmlIdClose="transactions-see-detail-close"
              htmlIdBackground="transactions-see-detail-close-bkgd"
            >
              <TransactionsDetailModalMobile
                handleCloseError={() => setShowModalTrxDetails(false)}
                headerData={headerData}
              />
            </Modal>
          ) : (
            <TransactionsDetailModal
              fClose={() => setShowModalTrxDetails(false)}
              headerData={headerData}
            />
          )}
        </>
      )}
    </>
  );
};

export default TransactionTableGral;
